import React from 'react';
import { Combobox } from '@base-ui/react/combobox';
import { LedgerSelectOption } from './LedgerSelect';
import { cn } from '../../lib/cn';

interface LedgerComboboxProps {
  value: string;
  options: LedgerSelectOption[];
  onValueChange: (value: string) => void;
  ariaLabel: string;
  /** Placeholder inside the filter box, e.g. "Search 58 risks". */
  searchLabel?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * A LedgerSelect with a filter box.
 *
 * Same props and the same trigger as LedgerSelect, so a picker can move over
 * without its consumer changing. The difference is the popup: it opens with a
 * text input that narrows the list as you type, which a plain select cannot
 * do. Worth it once a list is long enough that scanning it is the slow part,
 * which for this product is the risk register and an organization's full
 * membership.
 *
 * Options are { value, label } objects, the shape Base UI matches on
 * automatically, so the filter reads the label and the form gets the value.
 */
export const LedgerCombobox: React.FC<LedgerComboboxProps> = ({
  value,
  options,
  onValueChange,
  ariaLabel,
  searchLabel,
  className,
  disabled,
}) => {
  // A cold cache can seed a picker with an id whose options have not arrived,
  // and an id is not a label. Resolve it ourselves and fall back to the
  // placeholder entry (the one with value: '') the way LedgerSelect does.
  const selected = options.find((option) => option.value === value);
  const placeholder = options.find((option) => option.value === '');
  const triggerLabel = selected?.label ?? placeholder?.label ?? '—';

  // The placeholder is the way to clear a selection, so it belongs in the
  // list, but it should not be something you have to type past.
  const selectable = options.filter((option) => option.value !== '');

  return (
    <Combobox.Root
      items={selectable}
      value={selected ?? null}
      disabled={disabled}
      onValueChange={(next) => {
        onValueChange(next && typeof next === 'object' ? next.value : '');
      }}
    >
      <Combobox.Trigger
        aria-label={ariaLabel}
        className={cn(
          'inline-flex cursor-pointer items-baseline gap-1 text-scale-1 text-ink hover:text-accent',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      >
        <span className="truncate">{triggerLabel}</span>
        <span aria-hidden className="text-ink-muted">▾</span>
      </Combobox.Trigger>
      <Combobox.Portal>
        <Combobox.Positioner sideOffset={4} className="z-50">
          <Combobox.Popup className="flex max-h-72 w-72 flex-col border border-ink bg-paper-raised">
            <div className="border-b border-rule-faint p-1.5">
              <Combobox.Input
                aria-label={searchLabel ?? `Search ${ariaLabel}`}
                placeholder={searchLabel ?? 'Search'}
                className="w-full bg-transparent px-1 py-0.5 text-scale-1 text-ink outline-none placeholder:text-ink-muted"
              />
            </div>
            <Combobox.Empty className="px-3 py-2 font-serif text-scale-1 italic text-ink-muted">
              Nothing matches that.
            </Combobox.Empty>
            {/* Collection rather than a render-function child on List,
                because the placeholder is a sibling the filter must not
                touch: it is how you clear a selection, not a result. */}
            <Combobox.List className="overflow-y-auto py-1">
              {placeholder && (
                <Combobox.Item
                  value={placeholder}
                  className="cursor-pointer px-3 py-1 font-serif text-scale-1 italic text-ink-muted data-[highlighted]:bg-accent-soft"
                >
                  {placeholder.label}
                </Combobox.Item>
              )}
              <Combobox.Collection>
                {(option: LedgerSelectOption) => (
                  <Combobox.Item
                    key={option.value}
                    value={option}
                    className="cursor-pointer px-3 py-1 text-scale-1 text-ink data-[highlighted]:bg-accent-soft data-[selected]:font-medium"
                  >
                    {option.label}
                  </Combobox.Item>
                )}
              </Combobox.Collection>
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  );
};
